<script>
  import { _ } from '@sveltia/i18n';
  import { Menu, MenuButton, MenuItem, Spacer } from '@sveltia/ui';
  import { escapeRegExp } from '@sveltia/utils/string';
  import equal from 'fast-deep-equal';
  import { sanitize } from 'isomorphic-dompurify';
  import { parseInline } from 'marked';
  import { getContext, setContext } from 'svelte';
  import { writable } from 'svelte/store';

  import CopyMenuItems from '$lib/components/contents/details/editor/copy-menu-items.svelte';
  import FieldEditorGroup from '$lib/components/contents/details/editor/field-editor-group.svelte';
  import TranslateButton from '$lib/components/contents/details/editor/translate-button.svelte';
  import ValidationError from '$lib/components/contents/details/editor/validation-error.svelte';
  import { CustomEditor, editors } from '$lib/components/contents/details/fields';
  import { customFieldTypeRegistry } from '$lib/services/api/registries';
  import { entryDraft, INTERNAL_PROP_REGEX } from '$lib/services/contents/draft';
  import {
    resolveOriginalKeyPath,
    revertChanges,
  } from '$lib/services/contents/draft/update/revert';
  import { getValueMapSnapshot } from '$lib/services/contents/draft/value-map.svelte';
  import {
    getCurrentValue,
    getFieldKind,
    isFieldMultiple,
    isFieldRequired,
  } from '$lib/services/contents/entry/fields';
  import { DEFAULT_I18N_CONFIG } from '$lib/services/contents/i18n/config';
  import { isPendingDeletion } from '$lib/services/workflow';

  /**
   * @import { Component } from 'svelte';
   * @import { Writable } from 'svelte/store';
   * @import {
   * DraftValueStoreKey,
   * FieldContext,
   * FieldEditorContext,
   * InternalLocaleCode,
   * TypedFieldKeyPath,
   * } from '$lib/types/private';
   * @import {
   * BooleanField,
   * CustomField,
   * Field,
   * FieldKeyPath,
   * NumberField,
   * StringField,
   * VisibleField,
   * } from '$lib/types/public';
   */

  /** @type {FieldEditorContext} */
  const parent = getContext('field-editor') ?? {};

  /**
   * @typedef {object} Props
   * @property {InternalLocaleCode} locale Current pane’s locale.
   * @property {FieldKeyPath} keyPath Field key path.
   * @property {TypedFieldKeyPath} typedKeyPath Typed field key path.
   * @property {Field} fieldConfig Field configuration.
   * @property {FieldContext} [context] Where the field is rendered.
   * @property {string} [componentName] Name of the parent rich text editor component, if any.
   * @property {DraftValueStoreKey} [valueStoreKey] Key to store the values in {@link EntryDraft}.
   */

  /**
   * Options for {@link sanitize}.
   */
  const SANITIZE_OPTIONS = {
    ALLOWED_TAGS: ['strong', 'em', 'del', 'code', 'a', 'br'],
    ALLOWED_ATTR: ['href'],
  };

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const */
    locale,
    keyPath,
    typedKeyPath,
    fieldConfig,
    context: fieldContext = parent.fieldContext ?? undefined,
    componentName,
    valueStoreKey = parent.valueStoreKey ?? 'currentValues',
    /* eslint-enable prefer-const */
  } = $props();

  const fieldId = $props.id();

  /**
   * Parse the given string as Markdown and sanitize the result to only allow certain tags.
   * @param {string} str Original string.
   * @returns {string} Sanitized string.
   */
  const _sanitize = (str) =>
    sanitize(/** @type {string} */ (parseInline(str.replaceAll('\\n', '<br>'))), SANITIZE_OPTIONS);

  /**
   * Write a value coming from the widget editor back to the entry draft.
   *
   * The write is deferred to a microtask so that it happens outside Svelte’s update cycle. Widget
   * editors sync their local state to this binding from an `$effect`, and Svelte can only memoize
   * its dirty-marking traversal for writes made outside a running reaction. Without the deferral,
   * marking re-walks the derived graph once per path that reaches it, which is exponential in the
   * depth of the editor tree — tens of seconds per keystroke on a deeply nested entry.
   * @param {any} value New value.
   */
  const writeValue = (value) => {
    // Only primitive value fields support two-way binding. Updating an array and object in the
    // draft store has to be handled in each field editor.
    if (typeof value === 'object' && value !== null) {
      return;
    }

    const store = valueStoreKey;
    const _locale = locale;
    const _keyPath = keyPath;

    queueMicrotask(() => {
      entryDraft.update((draft) => {
        if (draft) {
          draft[store][_locale][_keyPath] = value;
        }

        return draft;
      });
    });
  };

  /** @type {Writable<Component>} */
  const extraHint = writable();

  setContext(
    'field-editor',
    // svelte-ignore state_referenced_locally
    /** @type {FieldEditorContext} */ ({
      fieldContext,
      parentComponentNames: [...(parent.parentComponentNames ?? []), componentName].filter(Boolean),
      extraHint,
      valueStoreKey,
    }),
  );

  const inEditorComponent = $derived(fieldContext === 'rich-text-editor-component');
  const { name: fieldName, widget: fieldType = 'string', i18n = false } = $derived(fieldConfig);
  const {
    label = '',
    comment = '',
    hint = '',
    readonly: readonlyOption = false,
  } = $derived(/** @type {VisibleField} */ (fieldConfig));
  const required = $derived(isFieldRequired({ fieldConfig, locale }));
  const multiple = $derived(isFieldMultiple(fieldConfig));
  const allowPrefix = $derived(['string'].includes(fieldType));
  const prefix = $derived(
    allowPrefix ? /** @type {StringField} */ (fieldConfig).prefix : undefined,
  );
  const suffix = $derived(
    allowPrefix ? /** @type {StringField} */ (fieldConfig).suffix : undefined,
  );
  const allowExtraLabels = $derived(['boolean', 'number', 'string'].includes(fieldType));
  const beforeInputLabel = $derived(
    allowExtraLabels
      ? /** @type {BooleanField | NumberField | StringField} */ (fieldConfig).before_input
      : undefined,
  );
  const afterInputLabel = $derived(
    allowExtraLabels
      ? /** @type {BooleanField | NumberField | StringField} */ (fieldConfig).after_input
      : undefined,
  );
  const hasExtraLabels = $derived(!!(prefix || suffix || beforeInputLabel || afterInputLabel));
  const isList = $derived(fieldType === 'list' || multiple);
  const collection = $derived($entryDraft?.collection);
  const collectionFile = $derived($entryDraft?.collectionFile);
  const originalValues = $derived($entryDraft?.originalValues);
  const { i18nEnabled, allLocales, defaultLocale } = $derived(
    (collectionFile ?? collection)?._i18n ?? DEFAULT_I18N_CONFIG,
  );
  const otherLocales = $derived(i18nEnabled ? allLocales.filter((l) => l !== locale) : []);
  const canTranslate = $derived(i18nEnabled && (i18n === true || i18n === 'translate'));
  const canDuplicate = $derived(i18nEnabled && i18n === 'duplicate');
  const canEdit = $derived(
    inEditorComponent || locale === defaultLocale || canTranslate || canDuplicate,
  );
  const canCopy = $derived(!inEditorComponent && canTranslate && otherLocales.length);
  const canRevert = $derived(!inEditorComponent && !(canDuplicate && locale !== defaultLocale));
  const keyPathRegex = $derived(new RegExp(`^${escapeRegExp(keyPath)}\\.\\d+$`));
  const valueMap = $derived(getValueMapSnapshot($entryDraft, locale, valueStoreKey));
  const customFieldType = $derived(customFieldTypeRegistry.get(fieldType));
  const currentValue = $derived(
    getCurrentValue({ valueMap, keyPath, isList, isCustomFieldType: !!customFieldType }),
  );
  const originalValue = $derived.by(() => {
    if (isList) {
      return Object.entries(originalValues?.[locale] ?? {})
        .filter(([_keyPath]) => keyPathRegex.test(_keyPath))
        .map(([, val]) => val)
        .filter((val) => val !== undefined);
    }

    // For fields inside list items, use the original key path if the item was reordered
    const currentMap = valueMap;
    const resolved = resolveOriginalKeyPath(currentMap, keyPath);

    if (resolved) {
      return originalValues?.[locale]?.[resolved.originalKeyPath];
    }

    return originalValues?.[locale]?.[keyPath];
  });
  const isRevertDisabled = $derived.by(() => {
    if (fieldType === 'list') {
      // For list fields, compare all flat entries under the keyPath prefix, because `currentValue`
      // and `originalValue` may not capture complex (nested) list items correctly
      const currentMap = valueMap;
      const originalMap = originalValues?.[locale] ?? {};
      const keyPathPrefix = `${keyPath}.`;

      const currentEntries = Object.entries(currentMap)
        .filter(([k]) => k.startsWith(keyPathPrefix) && !INTERNAL_PROP_REGEX.test(k))
        .sort(([a], [b]) => a.localeCompare(b));

      const originalEntries = Object.entries(originalMap)
        .filter(([k]) => k.startsWith(keyPathPrefix) && !INTERNAL_PROP_REGEX.test(k))
        .sort(([a], [b]) => a.localeCompare(b));

      return equal(currentEntries, originalEntries);
    }

    return equal(currentValue, originalValue);
  });
  const validity = $derived($entryDraft?.validities[locale][keyPath]);
  const fieldLabel = $derived(label || fieldName);
  // An entry awaiting deletion is shown for reference only. Unlike `readonly`, which is also set
  // for a duplicated locale, this hides the options that would change the content
  const pendingDeletion = $derived(isPendingDeletion($entryDraft?.originalEntry));
  const readonly = $derived(
    readonlyOption ||
      pendingDeletion ||
      (i18n === 'duplicate' && locale !== defaultLocale) ||
      fieldType === 'compute' ||
      fieldType === 'uuid',
  );
  const invalid = $derived(validity?.valid === false);
  const editorProps = $derived({
    locale,
    keyPath,
    typedKeyPath,
    fieldId,
    fieldLabel,
    fieldConfig,
    readonly,
    required,
    invalid,
  });
</script>

{#snippet beforeInput()}
  {#if beforeInputLabel}
    <div role="none" class="before-input">{@html _sanitize(beforeInputLabel)}</div>
  {/if}
  {#if prefix}
    <div role="none" class="prefix">{prefix}</div>
  {/if}
{/snippet}

{#snippet afterInput()}
  {#if suffix}
    <div role="none" class="suffix">{suffix}</div>
  {/if}
  {#if afterInputLabel}
    <div role="none" class="after-input">{@html _sanitize(afterInputLabel)}</div>
  {/if}
{/snippet}

{#if $entryDraft && canEdit && fieldType !== 'hidden'}
  <FieldEditorGroup
    aria-label={_('x_field', { values: { field: fieldLabel } })}
    data-field-type={fieldType}
    data-key-path={keyPath}
    data-typed-key-path={typedKeyPath}
    hidden={fieldType === 'compute'}
  >
    <header role="none">
      <h4 role="none" id="{fieldId}-label">{fieldLabel}</h4>
      {#if !readonly && required}
        <div class="required" aria-label={_('required')}>*</div>
      {/if}
      <Spacer flex />
      {#if canCopy && ['richtext', 'markdown', 'string', 'text', 'list', 'object'].includes(fieldType)}
        <TranslateButton size="small" {locale} {otherLocales} {keyPath} />
      {/if}
      {#if canCopy || canRevert}
        <MenuButton
          variant="ghost"
          size="small"
          iconic
          disabled={pendingDeletion}
          popupPosition="bottom-right"
          aria-label={_('show_field_options')}
        >
          {#snippet popup()}
            <Menu aria-label={_('field_options')}>
              {#if canCopy}
                <CopyMenuItems {locale} {otherLocales} {keyPath} />
              {/if}
              {#if canRevert}
                <MenuItem
                  label={_('revert_changes')}
                  disabled={isRevertDisabled}
                  onclick={() => {
                    revertChanges({ locale, keyPath });
                  }}
                />
              {/if}
            </Menu>
          {/snippet}
        </MenuButton>
      {/if}
    </header>
    {#if !readonly && comment}
      <div role="none" class="comment-wrapper">
        <p class="comment">{@html _sanitize(comment)}</p>
      </div>
    {/if}
    {#if validity?.valid === false}
      <ValidationError id="{fieldId}-error">
        {$entryDraft?.validationMessages[locale][keyPath]?.join(' ')}
      </ValidationError>
    {/if}
    <div role="none" class="field-wrapper" class:has-extra-labels={hasExtraLabels}>
      {#if customFieldType}
        {@render beforeInput()}
        <CustomEditor
          {...{ ...editorProps, fieldConfig: /** @type {CustomField} */ (fieldConfig) }}
          control={customFieldType.control}
          {currentValue}
        />
        {@render afterInput()}
      {:else if getFieldKind(fieldConfig) === 'unknown'}
        <div role="none">{_('unsupported_field_type_x', { values: { name: fieldType } })}</div>
      {:else}
        {@const Editor = editors[fieldType]}
        {@render beforeInput()}
        <Editor
          {...editorProps}
          bind:currentValue={() => currentValue, (value) => writeValue(value)}
        />
        {@render afterInput()}
      {/if}
    </div>
    {#if !readonly && (hint || $extraHint)}
      {@const ExtraHint = $extraHint}
      <div role="none" class="footer">
        {#if hint}
          <p class="hint">{@html _sanitize(hint)}</p>
        {/if}
        <ExtraHint {fieldConfig} {locale} {currentValue} />
      </div>
    {/if}
  </FieldEditorGroup>
{/if}

<style>
  .field-wrapper {
    &.has-extra-labels {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 4px;
    }

    :global {
      :is(input[type='text'], textarea) {
        width: 100%;
      }

      input:is([type='color'], [type='number']) {
        outline: 0;
        border-width: 1px;
        border-color: var(--sui-primary-border-color);
        border-radius: var(--sui-control-medium-border-radius);
        height: var(--sui-button-medium-height);
        color: inherit;
        background-color: var(--sui-textbox-background-color);
      }

      input:is([type='file'], [type='checkbox']) {
        color: inherit;
      }

      & > div {
        color: inherit;
      }

      input:is([type='date'], [type='datetime-local'], [type='time']) {
        outline: 0;
        margin: var(--sui-focus-ring-width);
        border-width: var(--sui-textbox-border-width, 1px);
        border-color: var(--sui-primary-border-color);
        border-radius: var(--sui-control-medium-border-radius);
        padding: var(--sui-textbox-singleline-padding);
        width: auto;
        height: var(--sui-textbox-height);
        color: var(--sui-textbox-foreground-color);
        background-color: var(--sui-textbox-background-color);
        font-family: var(--sui-textbox-font-family);
        font-size: var(--sui-textbox-font-size);
        text-transform: uppercase;

        &:disabled {
          opacity: 0.4;
        }
      }

      input[aria-invalid='true']:is(
          [type='color'],
          [type='date'],
          [type='datetime-local'],
          [type='time']
        ) {
        border-color: var(--sui-error-border-color);
      }

      input:read-only {
        /* Make readonly inputs selectable */
        -webkit-user-select: text;
        user-select: text;
        pointer-events: auto;
      }
    }
  }

  .before-input,
  .after-input,
  .prefix,
  .suffix {
    color: var(--sui-secondary-foreground-color);
    white-space: nowrap;
  }

  .comment,
  .hint {
    margin-inline: var(--sui-focus-ring-width) !important;
    font-size: var(--sui-font-size-small);
    line-height: var(--sui-line-height-compact);
  }

  .comment {
    margin-block: var(--sui-focus-ring-width) !important;
  }

  .hint {
    flex: auto;
    margin-block: var(--sui-focus-ring-width) 0 !important;
    color: var(--sui-tertiary-foreground-color);
  }

  .footer {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    margin-top: 4px;
  }
</style>
